import { type FC, type ReactNode } from "react";
import { AlertTriangle, Trash01 } from "@untitledui/icons";
import { Heading as AriaHeading } from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { cx } from "@/utils/cx";
import { Dialog, Modal as ModalSurface, ModalOverlay } from "./modal";

/**
 * Modal (controlled) + ConfirmModal — postavené NA oficiální UUI modal komponentě.
 *
 * Markup/struktura je převzatá 1:1 z oficiálních UUI šablon
 * (`form-01-modal`, `destructive/warning-stacked-left-aligned-modal`):
 *   ModalOverlay → Modal → Dialog → CloseButton (absolute) + header blok
 *   (volitelný FeaturedIcon + AriaHeading slot="title" + supporting text) +
 *   content + footer button-group. react-aria (přes ./modal primitiva) řeší
 *   overlay/animace/focus-trap/Esc/role=dialog; `slot="title"` automaticky
 *   nastaví aria-labelledby (žádný ruční useId).
 *
 * API zůstává zpětně kompatibilní s původním wrapperem (open/title/onClose/
 * children/footer; ConfirmModal message/destructive…) + nové volitelné
 * `icon`/`iconColor`/`description`/`size` pro plný UUI header.
 */

type ModalSize = "sm" | "md" | "lg";
const SIZE_CLASS: Record<ModalSize, string> = {
    sm: "sm:max-w-100", // ~400px — confirm / jednoduché
    md: "sm:max-w-128", // ~512px — default (odpovídá původnímu max-w-lg)
    lg: "sm:max-w-160", // ~640px — formuláře
};

type IconColor = "brand" | "gray" | "success" | "warning" | "error";

export interface ModalProps {
    open: boolean;
    title: string;
    onClose: () => void;
    children: ReactNode;
    footer?: ReactNode;
    /** Volitelná FeaturedIcon v hlavičce (UUI modal header). */
    icon?: FC<{ className?: string }>;
    /** Barva FeaturedIcon kroužku (default brand). */
    iconColor?: IconColor;
    /** Supporting text pod titulkem. */
    description?: ReactNode;
    /** Šířka modalu — sm (confirm) / md (default) / lg (formulář). */
    size?: ModalSize;
    /** Legacy prop — react-aria restoruje fokus na trigger nativně. */
    restoreFocus?: boolean;
}

export function Modal({
    open,
    title,
    onClose,
    children,
    footer,
    icon: Icon,
    iconColor = "brand",
    description,
    size = "md",
    restoreFocus = true,
}: ModalProps) {
    // restoreFocus: react-aria default (ModalOverlay vrací fokus na trigger).
    void restoreFocus;

    return (
        <ModalOverlay
            isOpen={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) onClose();
            }}
            isDismissable
        >
            <ModalSurface className={SIZE_CLASS[size]}>
                <Dialog>
                    <CloseButton
                        onPress={onClose}
                        theme="light"
                        size="sm"
                        label="Zavřít"
                        className="absolute top-3 right-3 sm:top-4 sm:right-4"
                    />
                    <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
                        {Icon && (
                            <div className="relative w-max">
                                <FeaturedIcon color={iconColor} size="md" theme="modern" icon={Icon} />
                            </div>
                        )}
                        <div className="z-10 flex flex-col gap-0.5 pr-8">
                            <AriaHeading slot="title" className="text-md font-semibold text-primary">
                                {title}
                            </AriaHeading>
                            {description && <p className="text-sm text-tertiary">{description}</p>}
                        </div>
                    </div>
                    <div className="px-4 py-5 text-sm text-secondary sm:px-6">{children}</div>
                    {footer && (
                        <div className="z-10 flex flex-col-reverse gap-3 p-4 pt-6 sm:flex-row sm:justify-end sm:px-6 sm:pt-8 sm:pb-6">
                            {footer}
                        </div>
                    )}
                </Dialog>
            </ModalSurface>
        </ModalOverlay>
    );
}

export interface ConfirmModalProps {
    open: boolean;
    title: string;
    message: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => void;
    onCancel: () => void;
    destructive?: boolean;
    /** Override default ikony (destructive=Trash01 / jinak AlertTriangle). */
    icon?: FC<{ className?: string }>;
}

export function ConfirmModal({
    open,
    title,
    message,
    confirmLabel = "Potvrdit",
    cancelLabel = "Zrušit",
    onConfirm,
    onCancel,
    destructive,
    icon,
}: ConfirmModalProps) {
    const HeaderIcon = icon ?? (destructive ? Trash01 : AlertTriangle);

    return (
        <ModalOverlay
            isOpen={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) onCancel();
            }}
            isDismissable
        >
            <ModalSurface className={cx(SIZE_CLASS.sm)}>
                <Dialog>
                    <CloseButton
                        onPress={onCancel}
                        theme="light"
                        size="sm"
                        label="Zavřít"
                        className="absolute top-3 right-3 sm:top-4 sm:right-4"
                    />
                    <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
                        <div className="relative w-max">
                            <FeaturedIcon color={destructive ? "error" : "warning"} size="md" theme="modern" icon={HeaderIcon} />
                        </div>
                        <div className="z-10 flex flex-col gap-0.5 pr-8">
                            <AriaHeading slot="title" className="text-md font-semibold text-primary">
                                {title}
                            </AriaHeading>
                            <p className="text-sm text-tertiary">{message}</p>
                        </div>
                    </div>
                    <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                        <Button color="secondary" size="md" onPress={onCancel}>
                            {cancelLabel}
                        </Button>
                        <Button color={destructive ? "primary-destructive" : "primary"} size="md" onPress={onConfirm}>
                            {confirmLabel}
                        </Button>
                    </div>
                </Dialog>
            </ModalSurface>
        </ModalOverlay>
    );
}
